import { CallableInstance, deepMerge } from './utils/index.mjs';
import { a as FexiosError, F as FexiosErrorCodes } from './shared/fexios.B-14qd8n.mjs';
import { FexiosHeaderBuilder } from './models/index.mjs';
import { F as FexiosQueryBuilder } from './shared/fexios.TxSvO_qH.mjs';
import { c as createFexiosResponse } from './shared/fexios.DFl5Gol9.mjs';
import { i as isPlainObject } from './shared/fexios.DtQgiOCE.mjs';

class Fexios extends CallableInstance {
  static version = "6.1.1";
  static FINAL_SYMBOL = Symbol("FEXIOS_FINAL_CONTEXT");
  baseConfigs;
  // for axios compatibility
  get defaults() {
    return this.baseConfigs;
  }
  set defaults(configs) {
    this.baseConfigs = configs;
  }
  static DEFAULT_CONFIGS = {
    baseURL: "",
    timeout: 0,
    credentials: void 0,
    headers: {},
    query: {},
    responseType: void 0,
    shouldThrow(response) {
      return !response.ok;
    },
    fetch: globalThis.fetch
  };
  hooks = [];
  static ALL_METHODS = [
    "get",
    "post",
    "put",
    "patch",
    "delete",
    "head",
    "options",
    "trace"
  ];
  static METHODS_WITHOUT_BODY = [
    "get",
    "head",
    "options",
    "trace"
  ];
  constructor(baseConfigs = {}) {
    super("request");
    this.baseConfigs = deepMerge(Fexios.DEFAULT_CONFIGS, baseConfigs);
    Fexios.ALL_METHODS.forEach(
      (m) => this.createMethodShortcut(m.toLowerCase())
    );
  }
  attachLegacyAliases(ctx) {
    const req = () => ctx.request;
    const rt = () => ctx.runtime;
    const define = (k, desc) => {
      try {
        Object.defineProperty(ctx, k, { configurable: true, ...desc });
      } catch {
      }
    };
    define("url", {
      get: () => req().url,
      set: (v) => {
        req().url = v?.toString?.() ?? String(v);
      }
    });
    define("method", {
      get: () => req().method,
      set: (v) => req().method = v
    });
    define("headers", {
      get: () => req().headers,
      set: (v) => req().headers = v
    });
    define("query", { get: () => req().query, set: (v) => req().query = v });
    define("body", { get: () => req().body, set: (v) => req().body = v });
    define("baseURL", {
      get: () => req().baseURL,
      set: (v) => req().baseURL = v
    });
    define("timeout", {
      get: () => req().timeout,
      set: (v) => req().timeout = v
    });
    define("credentials", {
      get: () => req().credentials,
      set: (v) => req().credentials = v
    });
    define("cache", { get: () => req().cache, set: (v) => req().cache = v });
    define("mode", { get: () => req().mode, set: (v) => req().mode = v });
    define("fetch", { get: () => req().fetch, set: (v) => req().fetch = v });
    define("shouldThrow", {
      get: () => req().shouldThrow,
      set: (v) => req().shouldThrow = v
    });
    define("responseType", {
      get: () => req().responseType,
      set: (v) => req().responseType = v
    });
    define("abortController", {
      get: () => rt().abortController,
      set: (v) => rt().abortController = v
    });
    define("onProgress", {
      get: () => rt().onProgress,
      set: (v) => rt().onProgress = v
    });
    define("customEnv", {
      get: () => rt().customEnv,
      set: (v) => rt().customEnv = v
    });
    define("rawRequest", {
      get: () => req().rawRequest,
      set: (v) => req().rawRequest = v
    });
    define("data", {
      get: () => ctx.response ? ctx.response.data : void 0,
      set: (v) => {
        if (ctx.response) ctx.response.data = v;
      }
    });
  }
  finalizeContext(ctx, fallbackURL) {
    const response = ctx.response;
    const rawResponse = response?.rawResponse ?? ctx.rawResponse;
    const req = ctx.request;
    Object.defineProperties(ctx, {
      url: { get: () => rawResponse?.url || fallbackURL },
      data: { get: () => response.data },
      headers: { get: () => rawResponse.headers },
      responseType: { get: () => response.responseType },
      rawRequest: { get: () => req.rawRequest }
    });
  }
  async request(urlOrOptions, options) {
    const thisApp = this;
    let reqInit = options || {};
    if (typeof urlOrOptions === "string" || urlOrOptions instanceof URL) {
      reqInit = { ...options || {}, url: urlOrOptions };
    } else if (typeof urlOrOptions === "object") {
      reqInit = urlOrOptions;
    }
    const {
      abortController: inputAbortController,
      onProgress,
      customEnv,
      ...requestOnly
    } = reqInit;
    let ctx = {
      get app() {
        return thisApp;
      },
      request: {
        ...requestOnly,
        url: reqInit.url?.toString?.() ?? String(reqInit.url)
      },
      runtime: {
        abortController: inputAbortController,
        onProgress,
        customEnv
      },
      response: void 0,
      rawResponse: void 0,
      // legacy fields are attached via defineProperty
      url: "",
      headers: {},
      query: {}
    };
    this.attachLegacyAliases(ctx);
    ctx = await this.emit("beforeInit", ctx);
    if (ctx[Fexios.FINAL_SYMBOL]) return ctx;
    if ("customEnv" in this.baseConfigs) {
      ctx.runtime.customEnv = deepMerge(
        {},
        // ensure we don't mutate baseConfigs
        this.baseConfigs.customEnv,
        ctx.runtime.customEnv
      );
    }
    ctx.request = this.applyDefaults(ctx.request);
    if (Fexios.METHODS_WITHOUT_BODY.includes(
      ctx.request.method?.toLocaleLowerCase?.()
    ) && ctx.request.body) {
      throw new FexiosError(
        FexiosErrorCodes.BODY_NOT_ALLOWED,
        `Request method "${ctx.request.method}" does not allow body`
      );
    }
    ctx = await this.emit("beforeRequest", ctx);
    if (ctx[Fexios.FINAL_SYMBOL]) return ctx;
    let body;
    const headerAutoPatch = {};
    const req = ctx.request;
    if (typeof req.body !== "undefined" && req.body !== null) {
      if (req.body instanceof Blob || req.body instanceof FormData || req.body instanceof URLSearchParams) {
        body = req.body;
      } else if (typeof req.body === "object" && req.body !== null) {
        body = JSON.stringify(req.body);
        req.headers = this.mergeHeaders(req.headers, {
          "Content-Type": "application/json"
        });
      } else {
        body = req.body;
      }
    }
    const optionsHeaders = FexiosHeaderBuilder.makeHeaders(req.headers || {});
    if (!optionsHeaders.get("content-type") && body) {
      if (body instanceof FormData || body instanceof URLSearchParams) {
        headerAutoPatch["content-type"] = null;
      } else if (typeof body === "string" && typeof req.body === "object") {
        headerAutoPatch["content-type"] = "application/json";
      } else if (body instanceof Blob) {
        headerAutoPatch["content-type"] = body.type || "application/octet-stream";
      }
    }
    req.body = body;
    ctx = await this.emit("afterBodyTransformed", ctx);
    if (ctx[Fexios.FINAL_SYMBOL]) return ctx;
    const abortController = ctx.runtime.abortController ?? (globalThis.AbortController ? new AbortController() : void 0);
    const fallback = globalThis.location?.href || "http://localhost";
    const baseForRequest = new URL(
      ctx.request.baseURL || this.baseConfigs.baseURL || fallback,
      fallback
    );
    const urlObjForRequest = new URL(ctx.request.url, baseForRequest);
    const finalURLForRequest = FexiosQueryBuilder.makeURL(
      urlObjForRequest,
      ctx.request.query,
      urlObjForRequest.hash
      // 保留 hash
    ).toString();
    const rawRequest = new Request(finalURLForRequest, {
      method: ctx.request.method || "GET",
      credentials: ctx.request.credentials,
      cache: ctx.request.cache,
      mode: ctx.request.mode,
      headers: FexiosHeaderBuilder.mergeHeaders(
        this.baseConfigs.headers,
        ctx.request.headers || {},
        headerAutoPatch
      ),
      body: ctx.request.body,
      signal: abortController?.signal
    });
    ctx.request.rawRequest = rawRequest;
    ctx = await this.emit("beforeActualFetch", ctx);
    if (ctx[Fexios.FINAL_SYMBOL]) return ctx;
    const timeout = ctx.request.timeout ?? this.baseConfigs.timeout ?? 60 * 1e3;
    const shouldThrow = ctx.request.shouldThrow ?? this.baseConfigs.shouldThrow;
    if (ctx.request.url.startsWith("ws") || ctx.request.responseType === "ws") {
      throw new FexiosError(
        FexiosErrorCodes.FEATURE_MOVED_TO_PLUGIN,
        `WebSocket support has been moved to plugins. Use "fexios/plugins" and call fx.ws() instead.`,
        ctx
      );
    }
    if (ctx.request.responseType === "stream") {
      throw new FexiosError(
        FexiosErrorCodes.FEATURE_MOVED_TO_PLUGIN,
        `SSE support has been moved to plugins. Use "fexios/plugins" and call fx.sse() instead.`,
        ctx
      );
    }
    let timer;
    try {
      if (abortController) {
        timer = timeout > 0 ? setTimeout(() => {
          abortController.abort();
        }, timeout) : void 0;
      }
      const fetch = ctx.request.fetch || this.baseConfigs.fetch || globalThis.fetch;
      const rawResponse = await fetch(ctx.request.rawRequest).catch(
        (err) => {
          if (timer) clearTimeout(timer);
          if (abortController?.signal.aborted) {
            throw new FexiosError(
              FexiosErrorCodes.TIMEOUT,
              `Request timed out after ${timeout}ms`,
              ctx
            );
          }
          throw new FexiosError(
            FexiosErrorCodes.NETWORK_ERROR,
            err.message,
            ctx
          );
        }
      );
      if (timer) clearTimeout(timer);
      ctx.rawResponse = rawResponse;
      await this.emit("afterRawResponse", ctx);
      if (ctx[Fexios.FINAL_SYMBOL]) return ctx;
      ctx.response = await createFexiosResponse(
        rawResponse,
        ctx.request.responseType,
        (progress, buffer) => {
          ctx.runtime.onProgress?.(progress, buffer);
        },
        shouldThrow,
        timeout
      );
      ctx.rawResponse = ctx.response.rawResponse;
      this.finalizeContext(ctx, finalURLForRequest);
      return this.emit("afterResponse", ctx);
    } catch (error) {
      if (timer) clearTimeout(timer);
      throw error;
    }
  }
  mergeQueries = FexiosQueryBuilder.mergeQueries;
  mergeHeaders = FexiosHeaderBuilder.mergeHeaders;
  applyDefaults(ctx) {
    const c = ctx;
    const fallback = globalThis.location?.href || "http://localhost";
    const effectiveBase = c.baseURL || this.baseConfigs.baseURL || fallback;
    const baseObj = new URL(effectiveBase, fallback);
    const reqURL = new URL(c.url.toString(), baseObj);
    const baseSearchParams = FexiosQueryBuilder.toQueryRecord(
      baseObj.searchParams
    );
    const reqSearchParams = FexiosQueryBuilder.toQueryRecord(
      reqURL.searchParams
    );
    const mergedSearchParams = FexiosQueryBuilder.mergeQueries(
      baseSearchParams,
      reqSearchParams
    );
    reqURL.search = FexiosQueryBuilder.makeSearchParams(mergedSearchParams).toString();
    c.url = reqURL.toString();
    const mergedQuery = FexiosQueryBuilder.mergeQueries(
      this.baseConfigs.query,
      c.query
    );
    if (c.query) {
      this.restoreNulls(mergedQuery, c.query);
    }
    c.query = mergedQuery;
    return c;
  }
  restoreNulls(target, source) {
    if (!source || typeof source !== "object") return;
    for (const [k, v] of Object.entries(source)) {
      if (v === null) {
        target[k] = null;
      } else if (isPlainObject(v)) {
        if (!target[k] || typeof target[k] !== "object") {
          target[k] = {};
        }
        this.restoreNulls(target[k], v);
      }
    }
  }
  async emit(event, ctx, opts = {
    shouldHandleShortCircuitResponse: true
  }) {
    const hooks = this.hooks.filter((h) => h.event === event);
    if (hooks.length === 0) return ctx;
    const shortCircuit = async (baseCtx, raw) => {
      const finalCtx = baseCtx;
      finalCtx.rawResponse = raw;
      if (!finalCtx.request?.rawRequest) {
        try {
          finalCtx.request.rawRequest = new Request(finalCtx.request.url, {
            method: finalCtx.request.method || "GET",
            headers: finalCtx.request.headers,
            body: finalCtx.request.body
          });
        } catch {
        }
      }
      const response = await createFexiosResponse(
        raw,
        baseCtx.request?.responseType,
        (progress, buffer) => baseCtx.runtime?.onProgress?.(progress, buffer),
        baseCtx.request?.shouldThrow ?? this.baseConfigs.shouldThrow,
        baseCtx.request?.timeout ?? this.baseConfigs.timeout ?? 60 * 1e3
      );
      finalCtx.response = response;
      finalCtx.rawResponse = response.rawResponse;
      this.finalizeContext(finalCtx, raw.url || "");
      if (event !== "afterResponse") {
        const after = await this.emit("afterResponse", finalCtx);
        after[Fexios.FINAL_SYMBOL] = true;
        return after;
      } else {
        finalCtx[Fexios.FINAL_SYMBOL] = true;
        return finalCtx;
      }
    };
    for (let i = 0; i < hooks.length; i++) {
      const hook = hooks[i];
      const hookName = `${String(event)}#${hook.action.name || `anonymous#${i}`}`;
      const marker = Symbol("FEXIOS_HOOK_CTX_MARK");
      try {
        ;
        ctx[marker] = marker;
      } catch {
      }
      const result = await hook.action.call(this, ctx);
      try {
        delete ctx[marker];
      } catch {
      }
      if (result === false) {
        throw new FexiosError(
          FexiosErrorCodes.ABORTED_BY_HOOK,
          `Request aborted by hook "${hookName}"`,
          ctx
        );
      }
      if (result instanceof Response) {
        if (opts.shouldHandleShortCircuitResponse !== false) {
          return shortCircuit(ctx, result);
        }
        ctx.rawResponse = result;
      } else if (result && typeof result === "object" && result[marker] === marker) {
        ctx = result;
      } else ;
    }
    return ctx;
  }
  on(event, action, prepend = false) {
    if (typeof action !== "function") {
      throw new FexiosError(
        FexiosErrorCodes.INVALID_HOOK_CALLBACK,
        `Hook should be a function, but got "${typeof action}"`
      );
    }
    this.hooks[prepend ? "unshift" : "push"]({
      event,
      action
    });
    return this;
  }
  off(event, action) {
    if (event === "*" || !event) {
      this.hooks = this.hooks.filter((hook) => hook.action !== action);
    } else {
      this.hooks = this.hooks.filter(
        (hook) => hook.event !== event || hook.action !== action
      );
    }
    return this;
  }
  createInterceptor(event) {
    return {
      handlers: () => this.hooks.filter((hook) => hook.event === event).map((hook) => hook.action),
      use: (hook, prepend = false) => {
        return this.on(event, hook, prepend);
      },
      clear: () => {
        this.hooks = this.hooks.filter((hook) => hook.event !== event);
      }
    };
  }
  interceptors = {
    request: this.createInterceptor("beforeRequest"),
    response: this.createInterceptor("afterResponse")
  };
  createMethodShortcut(method) {
    Reflect.defineProperty(this, method, {
      get: () => {
        return (url, bodyOrQuery, options) => {
          if (Fexios.METHODS_WITHOUT_BODY.includes(
            method.toLocaleLowerCase()
          )) {
            options = bodyOrQuery;
          } else {
            options = options || {};
            options.body = bodyOrQuery;
          }
          return this.request(url, {
            ...options,
            method
          });
        };
      }
    });
    return this;
  }
  extends(configs) {
    const fexios = new Fexios(deepMerge(this.baseConfigs, configs));
    fexios.hooks = [...this.hooks];
    fexios._plugins = new Map(this._plugins);
    fexios._plugins.forEach(async (plugin) => {
      await fexios.plugin(plugin);
    });
    return fexios;
  }
  create = Fexios.create;
  static create(configs) {
    return new Fexios(configs);
  }
  _plugins = /* @__PURE__ */ new Map();
  plugin(plugin) {
    if (typeof plugin?.name === "string" && typeof plugin?.install === "function") {
      if (this._plugins.has(plugin.name)) {
        return this;
      }
      const fx = plugin.install(this);
      this._plugins.set(plugin.name, plugin);
      if (fx instanceof Fexios) {
        return fx;
      }
    }
    return this;
  }
  uninstall(plugin) {
    if (typeof plugin === "string") {
      plugin = this._plugins.get(plugin);
    }
    if (plugin) {
      plugin?.uninstall?.(this);
      this._plugins.delete(plugin.name);
    }
    return this;
  }
  // 版本弃子们.jpg
  /** @deprecated Use `mergeQueries` instead */
  mergeQuery = this.mergeQueries;
}

export { Fexios };
//# sourceMappingURL=fexios.mjs.map
